import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import AutoComplete from 'components/field/AutoComplete';
import Button from 'components/field/Button';
import ImageInput from 'components/field/ImageInput';
import StringInput from 'components/field/StringInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import MinimizeButton from 'components/global/MinimizeButton';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListData } from 'components/list/dynamicListSlice';

import useAccessControl from 'hooks/useAccessControl';
import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useModalRouter from 'hooks/useModalRouter';
import { usePrompt } from 'hooks/usePrompt';

import WarehouseFormShort from 'pages/Warehouses/WarehouseFormShort';

import { AUTOCOMPLETE, BRAND } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';

import { StoreItems } from './StoreItems';

export const BrandForm = NiceModal.create(({ id, modalName, setBrand, mode }) => {
	const axios = useAxiosPrivate();
	const { showError, showSuccess } = useAlert();
	const [isLoading, setIsLoading] = useState(false);
	const dispatch = useDispatch();
	const modal = useModal();
	const { closeModal } = useModalRouter();
	const isAuthorized = useAccessControl();
	const formMode = useMemo(() => mode ?? 'add', [mode]);
	const [isMinimizing, setIsMinimizing] = useState(false);

	const handleCloseModal = useCallback(() => {
		if (modalName) {
			closeModal();
		} else {
			modal.remove();
		}
	}, [modalName]);

	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					logo: yup.string().nullable(),
					storeName: yup.string().nullable().required(),
					defaultWarehouseID: yup.number().nullable(),
					stores: yup.array().of(
						yup
							.object()
							.shape({
								storeName: yup.string().nullable(),
								salesChannelCredentialsID: yup.number().nullable()
							})
							.nullable()
							.required()
					)
				})
				.required(),
		[]
	);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			logo: null,
			storeName: null,
			defaultWarehouseID: null,
			stores: []
		}
	});

	const {
		handleSubmit,
		setValue,
		watch,
		reset,
		formState: { isDirty, isSubmitting, isSubmitSuccessful }
	} = formMethods;

	usePrompt(isDirty && !isSubmitting && !isSubmitSuccessful && !isMinimizing);

	const getBrandSettings = useCallback(() => {
		setIsLoading(true);
		axios
			.post(BRAND.GET_BRAND_SETTINGS, {
				ID: id
			})
			.then((response) => {
				const { data, status } = response;
				if (status === 200) {
					reset({
						logo: data.logo ?? null,
						storeName: data.storeName ?? null,
						defaultWarehouseID: data.defaultWarehouseID ?? null,
						stores: data?.stores?.map((item) => ({
							storeName: item?.storeName ?? null,
							salesChannelCredentialsID: item.salesChannelCredentialsID ?? null
						}))
					});
				} else {
					AppLogger.error(response);
					showError();
				}
			})
			.catch((error) => {
				AppLogger.error(error);
				showError();
			})
			.finally(() => {
				setIsLoading(false);
			});
	}, [id]);

	const onSubmit = (data) => {
		setIsLoading(true);
		axios
			.post(id ? BRAND.UPDATE_BRAND : BRAND.ADD_BRAND, {
				...(id ? { ID: id } : {}),
				...data
			})
			.then((response) => {
				if (response.status === 200) {
					showSuccess();
					if (setBrand) setBrand(response.data.ID);
					dispatch(updateDynamicListData(DYNAMICLISTS.BRANDS));
					handleCloseModal();
				} else {
					showError();
					AppLogger.error(response);
				}
			})
			.catch((error) => {
				showError({
					message: error?.response?.data?.code || error?.response?.data?.message
				});
				AppLogger.error(error);
			})
			.finally(() => {
				setIsLoading(false);
			});
	};

	useEffect(() => {
		if (id) {
			getBrandSettings();
		}
	}, [id]);

	const warehouseAutocompleteColumns = [
		{ accessor: 'Name', width: 200 },
		{ accessor: 'Code', width: 150 }
	];
	const warehouseAutocompleteLabels = useMemo(() => ['Name'], []);
	const defaultColSpan = {
		md: 6
	};

	return (
		<Modal
			fullwidth
			name={modalName}
			maxWidth="medium"
			className="modal-brand"
			minimizeButton={(show, close) => (
				<MinimizeButton
					onShow={() => {
						show();
						setIsMinimizing(false);
					}}
					onClose={close}
					label={formMode === 'add' ? 'addBrand' : formMode === 'edit' ? 'editBrand' : 'viewBrand'}
					tooltip={['edit', 'view'].includes(mode) ? watch('storeName') : ''}
				/>
			)}
			onMinimize={() => {
				setIsMinimizing(true);
			}}>
			<ModalTitle>
				<Typography variant="p1">
					{formMode === 'add' ? 'addBrand' : formMode === 'edit' ? 'editBrand' : 'viewBrand'}
				</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					{/* storeName */}
					<FormItem colSpan={defaultColSpan} label="storeName" isLoading={isLoading} required>
						<StringInput name="storeName" disabled={formMode === 'view'} />
					</FormItem>
					{/* storeName */}
					{/* logo */}
					<FormItem colSpan={defaultColSpan} label="logo" isLoading={isLoading}>
						<ImageInput
							name="logo"
							value={watch('logo')}
							disabled={formMode === 'view'}
							viewMode={formMode === 'view'}
							setValue={(value) => {
								setValue('logo', value, { shouldValidate: true, shouldDirty: true });
							}}
						/>
					</FormItem>
					{/* logo */}
					{/* warehouse */}
					<FormItem label="defaultWarehouse" isLoading={isLoading}>
						<AutoComplete
							autoCompleteName={AUTOCOMPLETE.WAREHOUSES}
							columns={warehouseAutocompleteColumns}
							selectionLabelAccessor={warehouseAutocompleteLabels}
							value={watch(`defaultWarehouseID`)}
							disabled={formMode === 'view'}
							setValue={(value) => {
								setValue(`defaultWarehouseID`, value?.ID ?? null, {
									shouldValidate: true,
									shouldDirty: true
								});
							}}
							firstItem={
								isAuthorized('/autocomplete/warehouse/addANewWarehouse')
									? {
											action: () =>
												NiceModal.show(WarehouseFormShort, {
													setWarehouse: (value) => {
														setValue(`defaultWarehouseID`, value, {
															shouldValidate: true,
															shouldDirty: true
														});
													}
												}),
											label: <Typography color="var(--blue)">addANewWarehouse</Typography>
										}
									: undefined
							}
						/>
					</FormItem>
					{/* warehouse */}
					{/* Stores */}
					<div className="mt-3 col-12">
						<Typography variant="p2" className="my-3">
							salesChannelsConnection
						</Typography>
						<StoreItems formMethods={formMethods} isLoading={isLoading} formMode={formMode} />
					</div>
					{/* Stores */}
				</Form>
			</ModalContent>
			<ModalActions>
				<Button label="cancel" variant="outline" onClick={handleCloseModal} />
				{formMode !== 'view' && (
					<Button
						label={formMode === 'add' ? 'button.add' : 'save'}
						disabled={!isDirty}
						disabledTooltip={!isDirty && 'formInfo.noChanges'}
						onClick={handleSubmit(onSubmit, (errors) => {
							AppLogger.log(errors);
						})}
						iconPos="right"
					/>
				)}
			</ModalActions>
		</Modal>
	);
});

export default BrandForm;
